/**
 * Open Orders Modal - Shows partial payment orders - Using new Modal component
 */
import React from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalTitle,
} from '../../components/ui/modal';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { ClipboardList, RefreshCw, Users, Table2, Clock, CreditCard } from 'lucide-react';

export function OpenOrdersModal({
  open,
  onOpenChange,
  orders,
  formatCurrency,
  onSelect,
  onRefresh,
}) {
  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPaidAmount = (order) => {
    if (!order.partial_payments) return 0;
    return order.partial_payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="lg">
        <ModalHeader className="flex flex-row items-center justify-between">
          <ModalTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Vendas em Aberto
          </ModalTitle>
          <Button variant="ghost" size="icon" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </ModalHeader>
        
        <ModalBody>
          <ScrollArea className="max-h-[60vh]">
            {orders.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma venda em aberto</p>
                <p className="text-sm">As vendas com pagamento parcial aparecerão aqui</p>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => {
                  const paidAmount = getPaidAmount(order);
                  const remaining = order.total - paidAmount;
                  
                  return (
                    <div
                      key={order.id}
                      className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => onSelect(order)}
                      data-testid={`open-order-${order.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-muted-foreground">
                            #{order.id?.slice(-6)}
                          </span>
                          <Badge variant="secondary">
                            <Clock className="h-3 w-3 mr-1" />
                            Parcial
                          </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatTime(order.created_at)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm mb-3">
                        {order.table_number && (
                          <span className="flex items-center gap-1">
                            <Table2 className="h-4 w-4 text-muted-foreground" />
                            Mesa {order.table_number}
                          </span>
                        )}
                        {order.customer_name && (
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            {order.customer_name}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="text-sm">
                          <span className="text-muted-foreground">Itens:</span>{' '}
                          <span className="font-medium">{order.items?.length || 0}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-mono font-bold text-primary">
                            {formatCurrency(order.total)}
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-emerald-500">
                              Pago: {formatCurrency(paidAmount)}
                            </span>
                            <span className="text-amber-500">
                              Resta: {formatCurrency(remaining)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Partial Payments History */}
                      {order.partial_payments?.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-muted-foreground mb-2">Pagamentos registrados:</p>
                          <div className="space-y-1">
                            {order.partial_payments.map((payment, idx) => (
                              <div key={idx} className="flex items-center justify-between text-xs">
                                <span className="flex items-center gap-1">
                                  <CreditCard className="h-3 w-3" />
                                  {payment.payer_name || 'Cliente'}
                                </span>
                                <span className="font-mono text-emerald-500">
                                  {formatCurrency(payment.amount)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default OpenOrdersModal;
